import React, { useState, useEffect, useRef } from 'react';
import { getBlogs } from './blogStorage';
import { getFAQs, deleteQuestion, addFAQ, updateFAQ } from './faqStorage';
import { getWhatsNew, addWhatsNew, updateWhatsNew, deleteWhatsNew } from './whatsNewStorage';
import BlogImage from '../assets/Blog_Page_Image.png';
import RichTextEditor from '../components/RichTextEditor';
import { sanitizeHtml, richTextProps } from '../utils/sanitizeHtml';
import { compressImageFileOrDataUrl, compressBlogContentImages } from '../utils/imageCompressor';
import './Admin.css';
import { endpoints } from '../config/api';

const defaultSeoMetadata = {
    tags: ['Productivity', 'Time Management'],
    primaryKeyword: ['multi calendar personal planner app'],
    secondaryKeywords: [
        'follow creator schedules',
        'time blocking task manager',
        'unified calendar app',
        'sync community events'
    ]
};

const parseChips = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(s => String(s).trim()).filter(Boolean);
    if (typeof val === 'string') {
        return val.split(',').map(s => s.trim().replace(/^#/, '')).filter(Boolean);
    }
    return [];
};

const getCurrentFormattedDate = () => {
    const date = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

const isWhatsNewVideo = (val) => {
    if (!val || typeof val !== 'string') return false;
    const clean = val.trim().toLowerCase();
    if (clean.startsWith('data:video/')) return true;
    if (/\.(mp4|webm|mov|ogg|m4v|mkv)($|\?)/i.test(clean)) return true;
    if (clean.includes('youtube.com') || clean.includes('youtu.be') || clean.includes('vimeo.com') || clean.includes('loom.com')) return true;
    if (clean.includes('video')) return true;
    return false;
};

const getWhatsNewEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const clean = url.trim();
    const ytMatch = clean.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i);
    if (ytMatch && ytMatch[1]) {
        return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    const vimeoMatch = clean.match(/vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|album\/(\d+)\/video\/|)(\d+)/i);
    if (vimeoMatch && vimeoMatch[3]) {
        return `https://player.vimeo.com/video/${vimeoMatch[3]}`;
    }
    const loomMatch = clean.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
    if (loomMatch && loomMatch[1]) {
        return `https://www.loom.com/embed/${loomMatch[1]}`;
    }
    return null;
};

const Admin = () => {

    // Authentication state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Check localStorage for saved authentication on mount
    useEffect(() => {
        // 1. Check for active session
        const savedAuth = localStorage.getItem('adminAuth');
        if (savedAuth) {
            try {
                const authData = JSON.parse(savedAuth);
                if (authData.isAuthenticated && authData.email) {
                    setIsAuthenticated(true);
                    setView('blogs'); // Ensure default view is blogs
                    setLoginData(prev => ({ ...prev, email: authData.email }));
                    // Show header first (instant, no scroll animation)
                    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                }
            } catch (e) {
                localStorage.removeItem('adminAuth');
            }
        } else {
            // 2. If not logged in, check for saved credentials to pre-fill
            const savedCreds = localStorage.getItem('savedCredentials');
            if (savedCreds) {
                try {
                    const creds = JSON.parse(savedCreds);
                    setLoginData(creds);
                    setRememberMe(true);
                } catch (e) {
                    localStorage.removeItem('savedCredentials');
                }
            }
        }
    }, []);

    // Blog management state
    const [blogs, setBlogs] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('Organiser'); // Default to Organiser
    const [viewingBlog, setViewingBlog] = useState(null); // For mini blog preview modal

    // FAQ State
    const [view, setView] = useState('blogs'); // 'blogs' or 'faqs'
    const [faqs, setFaqs] = useState([]);
    const [selectedFAQCategory, setSelectedFAQCategory] = useState('Organiser'); // FAQ specific filter


    // FAQ Form State
    const [isAddingFAQ, setIsAddingFAQ] = useState(false);
    const [isEditingFAQ, setIsEditingFAQ] = useState(false);
    const [isSavingFAQ, setIsSavingFAQ] = useState(false);
    const [selectedFAQ, setSelectedFAQ] = useState(null);
    const [faqFormData, setFaqFormData] = useState({
        question: '',
        answer: '',
        category: 'Organiser'
    });

    // What's New State
    const [whatsNewList, setWhatsNewList] = useState([]);
    const [selectedWhatsNewCategory, setSelectedWhatsNewCategory] = useState('Organiser');
    const [isAddingWhatsNew, setIsAddingWhatsNew] = useState(false);
    const [isEditingWhatsNew, setIsEditingWhatsNew] = useState(false);
    const [isSavingWhatsNew, setIsSavingWhatsNew] = useState(false);
    const [selectedWhatsNew, setSelectedWhatsNew] = useState(null);
    const [viewingFeedbackItem, setViewingFeedbackItem] = useState(null);
    const [customUpdateType, setCustomUpdateType] = useState('');
    const [isCustomType, setIsCustomType] = useState(false);
    const [customPlatform, setCustomPlatform] = useState('');
    const [isCustomPlatform, setIsCustomPlatform] = useState(false);
    const [videoPreviewError, setVideoPreviewError] = useState(false);
    const [whatsNewDescriptionError, setWhatsNewDescriptionError] = useState(false);
    const [whatsNewFormData, setWhatsNewFormData] = useState({
        title: '',
        category: 'Organiser',
        type: '',
        platform: '',
        date: getCurrentFormattedDate(),
        actions: '',
        description: '',
        media: '',
        mediaType: 'none'
    });

    // Change Password State
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordFormData, setPasswordFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Password visibility toggles
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Load blogs and FAQs on mount
    const fetchBlogs = async () => {
        const data = await getBlogs();
        setBlogs(data);
    };

    useEffect(() => {
        fetchBlogs();

        const loadFAQs = async () => {
            const loadedFAQs = await getFAQs();
            // Reverse the array so newest FAQs appear first
            setFaqs(loadedFAQs.reverse());
        };
        loadFAQs();

        const loadWhatsNew = async () => {
            const loaded = await getWhatsNew();
            setWhatsNewList(loaded);
        };
        loadWhatsNew();

        const handleStorageUpdate = () => loadFAQs();
        const handleWhatsNewUpdate = () => loadWhatsNew();

        window.addEventListener('faq-storage-update', handleStorageUpdate);
        window.addEventListener('whatsnew-storage-update', handleWhatsNewUpdate);

        let channel;
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                channel = new BroadcastChannel('fragment_whatsnew_channel');
                channel.onmessage = (event) => {
                    if (event.data?.type === 'update') {
                        loadWhatsNew();
                    }
                };
            }
        } catch (e) {}

        return () => {
            window.removeEventListener('faq-storage-update', handleStorageUpdate);
            window.removeEventListener('whatsnew-storage-update', handleWhatsNewUpdate);
            try {
                if (channel) channel.close();
            } catch (e) {}
        };
    }, []);

    const [selectedBlog, setSelectedBlog] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSavingBlog, setIsSavingBlog] = useState(false);
    const [isDeletingBlog, setIsDeletingBlog] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Bulk Selection States
    const [selectedBlogIds, setSelectedBlogIds] = useState([]);
    const [selectedFaqIds, setSelectedFaqIds] = useState([]);
    const [selectedWhatsNewIds, setSelectedWhatsNewIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        date: getCurrentFormattedDate(),
        summary: '',
        content: '',
        category: 'Organiser',
        tags: [...defaultSeoMetadata.tags],
        primaryKeyword: [...defaultSeoMetadata.primaryKeyword],
        secondaryKeywords: [...defaultSeoMetadata.secondaryKeywords]
    });

    // Tag and Keyword chip input states
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [tagInputValue, setTagInputValue] = useState('');
    const tagInputRef = useRef(null);

    const [isAddingPrimaryKeyword, setIsAddingPrimaryKeyword] = useState(false);
    const [primaryKeywordInputValue, setPrimaryKeywordInputValue] = useState('');
    const primaryKeywordInputRef = useRef(null);

    const [isAddingKeyword, setIsAddingKeyword] = useState(false);
    const [keywordInputValue, setKeywordInputValue] = useState('');
    const keywordInputRef = useRef(null);

    // Handle login input changes
    const handleLoginInputChange = (e) => {
        const { name, value } = e.target;
        setLoginData(prev => ({
            ...prev,
            [name]: value
        }));
        setLoginError(''); // Clear error when user types
    };

    // Handle login submission
    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoggingIn(true);
        setLoginError('');

        try {
            const response = await fetch(endpoints.adminLogin(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(loginData),
            });

            const data = await response.json();

            if (data.success) {
                setIsAuthenticated(true);
                setView('blogs'); // Force default view to Blogs
                setLoginError('');

                // Show header first (instant, no scroll animation)
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

                // Save session state (Auto-login)
                if (rememberMe) {
                    localStorage.setItem('adminAuth', JSON.stringify({
                        isAuthenticated: true,
                        email: loginData.email
                    }));
                }

                // Save credentials for pre-fill (separate from session)
                if (rememberMe) {
                    localStorage.setItem('savedCredentials', JSON.stringify(loginData));
                } else {
                    localStorage.removeItem('savedCredentials');
                }

            } else {
                setLoginError(data.message || 'Invalid email or password');
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginError('Server error. Please try again later.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Handle logout - clears session but keeps credentials if they were saved
    const handleLogout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuth'); // End session

        // Check if we have saved credentials to restore
        const savedCreds = localStorage.getItem('savedCredentials');
        if (savedCreds) {
            try {
                const creds = JSON.parse(savedCreds);
                setLoginData(creds);
                setRememberMe(true);
            } catch (e) {
                setLoginData({ email: '', password: '' });
                setRememberMe(false);
            }
        } else {
            setLoginData({ email: '', password: '' });
            setRememberMe(false);
        }
    };

    // Handle full logout - clears all saved credentials and session
    const handleForgetMe = () => {
        setIsAuthenticated(false);
        setLoginData({ email: '', password: '' });
        setRememberMe(false);
        localStorage.removeItem('adminAuth');
        localStorage.removeItem('savedCredentials');
    };

    // Change Password Handlers
    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setPasswordError('');
        setPasswordSuccess('');
    };

    const resetPasswordForm = () => {
        setPasswordFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordError('');
        setPasswordSuccess('');
        setShowChangePassword(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        // Validate passwords match
        if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        // Validate password length
        if (passwordFormData.newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long');
            return;
        }

        setIsChangingPassword(true);

        try {
            const response = await fetch(endpoints.adminChangePassword(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: loginData.email,
                    currentPassword: passwordFormData.currentPassword,
                    newPassword: passwordFormData.newPassword
                }),
            });

            const data = await response.json();

            if (data.success) {
                setPasswordSuccess('Password changed successfully!');
                // Clear the form after success
                setTimeout(() => {
                    resetPasswordForm();
                }, 2000);
            } else {
                setPasswordError(data.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Change password error:', error);
            setPasswordError('Server error. Please try again later.');
        } finally {
            setIsChangingPassword(false);
        }
    };


    // Tag management helpers
    const handleAddTag = () => {
        const trimmed = tagInputValue.trim().replace(/^#/, '');
        if (!trimmed) {
            setIsAddingTag(false);
            return;
        }
        const newTags = trimmed.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);
        setFormData(prev => {
            const currentTags = Array.isArray(prev.tags) ? prev.tags : parseChips(prev.tags);
            const combined = [...currentTags];
            newTags.forEach(tag => {
                if (!combined.some(existing => existing.toLowerCase() === tag.toLowerCase())) {
                    combined.push(tag);
                }
            });
            return { ...prev, tags: combined };
        });
        setTagInputValue('');
    };

    const handleRemoveTag = (indexToRemove) => {
        setFormData(prev => {
            const currentTags = Array.isArray(prev.tags) ? prev.tags : parseChips(prev.tags);
            return {
                ...prev,
                tags: currentTags.filter((_, idx) => idx !== indexToRemove)
            };
        });
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddTag();
        } else if (e.key === 'Escape') {
            setIsAddingTag(false);
            setTagInputValue('');
        } else if (e.key === 'Backspace' && !tagInputValue) {
            const currentTags = Array.isArray(formData.tags) ? formData.tags : parseChips(formData.tags);
            if (currentTags.length > 0) {
                handleRemoveTag(currentTags.length - 1);
            }
        }
    };

    // Keyword management helpers
    const handleAddKeyword = () => {
        const trimmed = keywordInputValue.trim();
        if (!trimmed) {
            setIsAddingKeyword(false);
            return;
        }
        const newKeywords = trimmed.split(',').map(k => k.trim()).filter(Boolean);
        setFormData(prev => {
            const currentKeywords = Array.isArray(prev.secondaryKeywords) ? prev.secondaryKeywords : parseChips(prev.secondaryKeywords);
            const combined = [...currentKeywords];
            newKeywords.forEach(kw => {
                if (!combined.some(existing => existing.toLowerCase() === kw.toLowerCase())) {
                    combined.push(kw);
                }
            });
            return { ...prev, secondaryKeywords: combined };
        });
        setKeywordInputValue('');
    };

    const handleRemoveKeyword = (indexToRemove) => {
        setFormData(prev => {
            const currentKeywords = Array.isArray(prev.secondaryKeywords) ? prev.secondaryKeywords : parseChips(prev.secondaryKeywords);
            return {
                ...prev,
                secondaryKeywords: currentKeywords.filter((_, idx) => idx !== indexToRemove)
            };
        });
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddKeyword();
        } else if (e.key === 'Escape') {
            setIsAddingKeyword(false);
            setKeywordInputValue('');
        } else if (e.key === 'Backspace' && !keywordInputValue) {
            const currentKeywords = Array.isArray(formData.secondaryKeywords) ? formData.secondaryKeywords : parseChips(formData.secondaryKeywords);
            if (currentKeywords.length > 0) {
                handleRemoveKeyword(currentKeywords.length - 1);
            }
        }
    };

    // Primary Keyword helpers
    const handleAddPrimaryKeyword = () => {
        const trimmed = primaryKeywordInputValue.trim();
        if (!trimmed) {
            setIsAddingPrimaryKeyword(false);
            return;
        }
        const newKeywords = trimmed.split(',').map(k => k.trim()).filter(Boolean);
        setFormData(prev => {
            const current = Array.isArray(prev.primaryKeyword) ? prev.primaryKeyword : parseChips(prev.primaryKeyword);
            const combined = [...current];
            newKeywords.forEach(kw => {
                if (!combined.some(existing => existing.toLowerCase() === kw.toLowerCase())) {
                    combined.push(kw);
                }
            });
            return { ...prev, primaryKeyword: combined };
        });
        setPrimaryKeywordInputValue('');
    };

    const handleRemovePrimaryKeyword = (indexToRemove) => {
        setFormData(prev => {
            const current = Array.isArray(prev.primaryKeyword) ? prev.primaryKeyword : parseChips(prev.primaryKeyword);
            return {
                ...prev,
                primaryKeyword: current.filter((_, idx) => idx !== indexToRemove)
            };
        });
    };

    const handlePrimaryKeywordKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddPrimaryKeyword();
        } else if (e.key === 'Escape') {
            setIsAddingPrimaryKeyword(false);
            setPrimaryKeywordInputValue('');
        } else if (e.key === 'Backspace' && !primaryKeywordInputValue) {
            const current = Array.isArray(formData.primaryKeyword) ? formData.primaryKeyword : parseChips(formData.primaryKeyword);
            if (current.length > 0) {
                handleRemovePrimaryKeyword(current.length - 1);
            }
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            date: getCurrentFormattedDate(),
            summary: '',
            content: '',
            category: selectedCategory,
            image: null,
            tags: [...defaultSeoMetadata.tags],
            primaryKeyword: [...defaultSeoMetadata.primaryKeyword],
            secondaryKeywords: [...defaultSeoMetadata.secondaryKeywords]
        });
        setSelectedBlog(null);
        setIsAdding(false);
        setIsEditing(false);
        setIsSavingBlog(false);
        setIsDeletingBlog(false);
        setShowDeleteConfirm(false);
        setIsAddingTag(false);
        setTagInputValue('');
        setIsAddingPrimaryKeyword(false);
        setPrimaryKeywordInputValue('');
        setIsAddingKeyword(false);
        setKeywordInputValue('');
    };

    const handleAddBlogClick = () => {
        setFormData({
            title: '',
            date: getCurrentFormattedDate(),
            summary: '',
            content: '',
            category: selectedCategory,
            image: null,
            tags: [...defaultSeoMetadata.tags],
            primaryKeyword: [...defaultSeoMetadata.primaryKeyword],
            secondaryKeywords: [...defaultSeoMetadata.secondaryKeywords]
        });
        setSelectedBlog(null);
        setIsAdding(true);
        setIsEditing(false);
        setShowDeleteConfirm(false);
        setIsAddingTag(false);
        setTagInputValue('');
        setIsAddingPrimaryKeyword(false);
        setPrimaryKeywordInputValue('');
        setIsAddingKeyword(false);
        setKeywordInputValue('');
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle image file selection
    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressed = await compressImageFileOrDataUrl(file, 1400, 1000, 0.82);
                setFormData(prev => ({
                    ...prev,
                    image: compressed
                }));
            } catch (err) {
                console.warn('Could not compress image, falling back to raw file:', err);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData(prev => ({
                        ...prev,
                        image: reader.result
                    }));
                };
                reader.readAsDataURL(file);
            }
        }
    };

    // Handle remove image
    const handleRemoveImage = () => {
        setFormData(prev => ({
            ...prev,
            image: 'NONE'
        }));
        // Clear file input
        const fileInput = document.getElementById('blog-image-input');
        if (fileInput) fileInput.value = '';
    };

    // Handle rich text content changes
    const handleContentChange = (htmlContent) => {
        setFormData(prev => ({
            ...prev,
            content: htmlContent
        }));
    };

    // Add new blog
    const handleAddBlog = async () => {
        if (isSavingBlog) return;
        if (!formData.title.trim()) {
            alert('Please enter a blog title');
            return;
        }

        setIsSavingBlog(true);
        try {
            // Compress inline images and cover image to ensure lightweight payload
            const optimizedContent = await compressBlogContentImages(formData.content);
            const isValidCover = formData.image && typeof formData.image === 'string' && formData.image.trim() !== '' && formData.image !== 'NONE' && formData.image !== 'NO_IMAGE' && formData.image !== BlogImage && !formData.image.includes('/static/media/') && !formData.image.includes('Blog_Page_Image');
            const optimizedCover = isValidCover
                ? await compressImageFileOrDataUrl(formData.image, 1400, 1000, 0.82)
                : 'NONE';

            const tagsString = Array.isArray(formData.tags) ? formData.tags.join(', ') : (formData.tags || '');
            const primaryKeywordString = Array.isArray(formData.primaryKeyword) ? formData.primaryKeyword.join(', ') : (formData.primaryKeyword || '');
            const secondaryKeywordsString = Array.isArray(formData.secondaryKeywords) ? formData.secondaryKeywords.join(', ') : (formData.secondaryKeywords || '');

            const newBlog = {
                title: formData.title,
                date: formData.date,
                summary: formData.summary,
                content: sanitizeHtml(optimizedContent),
                image: optimizedCover,
                category: formData.category,
                tags: tagsString,
                primaryKeyword: primaryKeywordString,
                secondaryKeywords: secondaryKeywordsString,
                keywords: {
                    primary: primaryKeywordString,
                    secondary: secondaryKeywordsString
                }
            };

            const response = await fetch(endpoints.blogs(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newBlog)
            });
            if (response.ok) {
                try {
                    localStorage.setItem('fragment_blog_cache_version', Date.now().toString());
                } catch (e) {}
                await fetchBlogs();
                resetForm();
                alert('Blog added successfully!');
            } else {
                const errData = await response.json().catch(() => null);
                const errMsg = errData?.message || `Server error (${response.status})`;
                alert('Failed to add blog: ' + errMsg);
            }
        } catch (error) {
            console.error('Error adding blog:', error);
            if (error.message && error.message.includes('Failed to fetch')) {
                alert('Error adding blog: The blog content may be too large. Try reducing image sizes or use smaller images.');
            } else {
                alert('Error adding blog: ' + error.message);
            }
        } finally {
            setIsSavingBlog(false);
        }
    };

    // Edit existing blog
    const handleEditClick = async (blog) => {
        let fullBlog = blog;
        if ((!blog.content || blog.content === '') && blog._id) {
            try {
                const res = await fetch(endpoints.blogById(blog._id));
                if (res.ok) {
                    fullBlog = await res.json();
                }
            } catch (err) {
                console.error('Error fetching full blog for editing:', err);
            }
        }
        setSelectedBlog(fullBlog);
        const parsedTags = parseChips(fullBlog.tags);
        const parsedPrimary = parseChips(fullBlog.primaryKeyword || (fullBlog.keywords && fullBlog.keywords.primary));
        const parsedSecondary = parseChips(fullBlog.secondaryKeywords || (fullBlog.keywords && fullBlog.keywords.secondary));

        setFormData({
            title: fullBlog.title,
            date: getCurrentFormattedDate(),
            summary: fullBlog.summary,
            content: fullBlog.content || '', // Keep HTML content for rich text editor
            image: (fullBlog.image && fullBlog.image !== 'NONE' && fullBlog.image !== 'NO_IMAGE' && !fullBlog.image.includes('/static/media/') && !fullBlog.image.includes('Blog_Page_Image')) ? fullBlog.image : '',
            category: fullBlog.category || selectedCategory,
            tags: parsedTags.length > 0 ? parsedTags : (fullBlog.tags === '' ? [] : [...defaultSeoMetadata.tags]),
            primaryKeyword: parsedPrimary.length > 0 ? parsedPrimary : (fullBlog.primaryKeyword === '' ? [] : [...defaultSeoMetadata.primaryKeyword]),
            secondaryKeywords: parsedSecondary.length > 0 ? parsedSecondary : (fullBlog.secondaryKeywords === '' ? [] : [...defaultSeoMetadata.secondaryKeywords])
        });
        setIsEditing(true);
        setIsAdding(false);
        setIsAddingTag(false);
        setTagInputValue('');
        setIsAddingPrimaryKeyword(false);
        setPrimaryKeywordInputValue('');
        setIsAddingKeyword(false);
        setKeywordInputValue('');
    };

    // Save edited blog
    const handleSaveEdit = async () => {
        if (isSavingBlog) return;
        setIsSavingBlog(true);
        try {
            // Compress inline images and cover image to ensure lightweight payload
            const optimizedContent = await compressBlogContentImages(formData.content);
            const rawCover = formData.image !== undefined && formData.image !== null ? formData.image : (selectedBlog.image || '');
            const isValidCover = rawCover && typeof rawCover === 'string' && rawCover.trim() !== '' && rawCover !== 'NONE' && rawCover !== 'NO_IMAGE' && rawCover !== BlogImage && !rawCover.includes('/static/media/') && !rawCover.includes('Blog_Page_Image') && !rawCover.includes('UExBQ0VIT0xERVJfSU1BR0');
            const optimizedCover = isValidCover ? await compressImageFileOrDataUrl(rawCover, 1400, 1000, 0.82) : 'NONE';

            const tagsString = Array.isArray(formData.tags) ? formData.tags.join(', ') : (formData.tags || '');
            const primaryKeywordString = Array.isArray(formData.primaryKeyword) ? formData.primaryKeyword.join(', ') : (formData.primaryKeyword || '');
            const secondaryKeywordsString = Array.isArray(formData.secondaryKeywords) ? formData.secondaryKeywords.join(', ') : (formData.secondaryKeywords || '');

            const updatedBlog = {
                title: formData.title,
                date: formData.date,
                summary: formData.summary,
                content: sanitizeHtml(optimizedContent),
                image: optimizedCover,
                category: formData.category,
                tags: tagsString,
                primaryKeyword: primaryKeywordString,
                secondaryKeywords: secondaryKeywordsString,
                keywords: {
                    primary: primaryKeywordString,
                    secondary: secondaryKeywordsString
                }
            };

            const response = await fetch(endpoints.blogById(selectedBlog._id), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedBlog)
            });
            if (response.ok) {
                try {
                    localStorage.setItem('fragment_blog_cache_version', Date.now().toString());
                } catch (e) {}
                await fetchBlogs();
                resetForm();
                alert('Blog updated successfully!');
            } else {
                const errData = await response.json().catch(() => null);
                const errMsg = errData?.message || `Server error (${response.status})`;
                alert('Failed to update blog: ' + errMsg);
            }
        } catch (error) {
            console.error('Error updating blog:', error);
            if (error.message && error.message.includes('Failed to fetch')) {
                alert('Error updating blog: The blog content may be too large. Try reducing image sizes or use smaller images.');
            } else {
                alert('Error updating blog: ' + error.message);
            }
        } finally {
            setIsSavingBlog(false);
        }
    };

    // Delete blog
    const handleDeleteClick = (blog) => {
        setSelectedBlog(blog);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (isDeletingBlog) return;
        setIsDeletingBlog(true);
        try {
            const response = await fetch(endpoints.blogById(selectedBlog._id), {
                method: 'DELETE'
            });
            if (response.ok) {
                try {
                    localStorage.setItem('fragment_blog_cache_version', Date.now().toString());
                } catch (e) {}
                if (selectedBlog?._id) {
                    setSelectedBlogIds(prev => prev.filter(id => id !== selectedBlog._id));
                }
                await fetchBlogs();
                resetForm();
            } else {
                alert('Failed to delete blog');
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
            alert('Error deleting blog');
        } finally {
            setIsDeletingBlog(false);
        }
    };

    // Blog Bulk Selection Handlers
    const handleToggleSelectBlog = (id) => {
        if (!id) return;
        setSelectedBlogIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleToggleSelectAllBlogs = () => {
        const currentCategoryBlogs = blogs.filter(b => b.category === selectedCategory);
        const allIds = currentCategoryBlogs.map(b => b._id).filter(Boolean);
        if (allIds.length === 0) return;
        const isAllSelected = allIds.every(id => selectedBlogIds.includes(id));
        if (isAllSelected) {
            setSelectedBlogIds(prev => prev.filter(id => !allIds.includes(id)));
        } else {
            setSelectedBlogIds(prev => Array.from(new Set([...prev, ...allIds])));
        }
    };

    const handleBulkDeleteBlogs = async () => {
        if (selectedBlogIds.length === 0 || isBulkDeleting) return;
        const count = selectedBlogIds.length;
        if (!window.confirm(`Are you sure you want to delete ${count} selected blog(s)? This action cannot be undone.`)) {
            return;
        }
        setIsBulkDeleting(true);
        try {
            await Promise.all(
                selectedBlogIds.map(id =>
                    fetch(endpoints.blogById(id), { method: 'DELETE' }).catch(err => {
                        console.error(`Failed to delete blog ${id}:`, err);
                        return null;
                    })
                )
            );
            setSelectedBlogIds([]);
            try {
                localStorage.setItem('fragment_blog_cache_version', Date.now().toString());
            } catch (e) {}
            await fetchBlogs();
            alert(`Successfully deleted ${count} blog(s).`);
        } catch (error) {
            console.error('Error bulk deleting blogs:', error);
            alert('An error occurred during bulk deletion.');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // View blog - opens mini preview modal
    const handleViewBlog = async (id) => {
        let blog = blogs.find(b => b._id === id);
        if (blog && (!blog.content || blog.content === '') && blog._id) {
            try {
                const res = await fetch(endpoints.blogById(blog._id));
                if (res.ok) {
                    blog = await res.json();
                }
            } catch (err) {
                console.error('Error fetching full blog for preview:', err);
            }
        }
        if (blog) {
            setViewingBlog(blog);
        }
    };

    // FAQ Handlers


    const handleDeleteFAQ = async (id) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            await deleteQuestion(id);
            setSelectedFaqIds(prev => prev.filter(itemId => itemId !== id));
            const updated = await getFAQs();
            setFaqs(updated.reverse());
        }
    };

    // FAQ Bulk Selection Handlers
    const handleToggleSelectFaq = (id) => {
        if (!id) return;
        setSelectedFaqIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleToggleSelectAllFaqs = () => {
        const currentCategoryFaqs = faqs.filter(f => f.category === selectedFAQCategory);
        const allIds = currentCategoryFaqs.map(f => f._id).filter(Boolean);
        if (allIds.length === 0) return;
        const isAllSelected = allIds.every(id => selectedFaqIds.includes(id));
        if (isAllSelected) {
            setSelectedFaqIds(prev => prev.filter(id => !allIds.includes(id)));
        } else {
            setSelectedFaqIds(prev => Array.from(new Set([...prev, ...allIds])));
        }
    };

    const handleBulkDeleteFaqs = async () => {
        if (selectedFaqIds.length === 0 || isBulkDeleting) return;
        const count = selectedFaqIds.length;
        if (!window.confirm(`Are you sure you want to delete ${count} selected FAQ(s)? This action cannot be undone.`)) {
            return;
        }
        setIsBulkDeleting(true);
        try {
            await Promise.all(
                selectedFaqIds.map(id =>
                    deleteQuestion(id).catch(err => {
                        console.error(`Failed to delete FAQ ${id}:`, err);
                        return null;
                    })
                )
            );
            setSelectedFaqIds([]);
            const updated = await getFAQs();
            setFaqs(updated.reverse());
            alert(`Successfully deleted ${count} FAQ(s).`);
        } catch (error) {
            console.error('Error bulk deleting FAQs:', error);
            alert('An error occurred during bulk deletion.');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // FAQ Form Handlers
    const resetFAQForm = () => {
        setFaqFormData({
            question: '',
            answer: '',
            category: selectedFAQCategory
        });
        setSelectedFAQ(null);
        setIsAddingFAQ(false);
        setIsEditingFAQ(false);
        setIsSavingFAQ(false);
    };

    const handleFAQInputChange = (e) => {
        const { name, value } = e.target;
        setFaqFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFAQContentChange = (htmlContent) => {
        setFaqFormData(prev => ({
            ...prev,
            answer: htmlContent
        }));
    };

    const handleAddFAQClick = () => {
        setFaqFormData({
            question: '',
            answer: '',
            category: selectedFAQCategory
        });
        setIsAddingFAQ(true);
        setIsEditingFAQ(false);
    };

    const handleEditFAQClick = (faq) => {
        setSelectedFAQ(faq);
        setFaqFormData({
            question: faq.question,
            answer: faq.answer || '',
            category: faq.category || 'Organiser'
        });
        setIsEditingFAQ(true);
        setIsAddingFAQ(false);
    };

    const handleSaveFAQ = async () => {
        if (isSavingFAQ) return;
        if (!faqFormData.question || !faqFormData.question.trim()) {
            alert('Please enter a question');
            return;
        }

        setIsSavingFAQ(true);
        try {
            if (isAddingFAQ) {
                // Ensure status is 'answered' so it shows up on Demo page
                const payload = { ...faqFormData, status: 'answered' };
                await addFAQ(payload);
                alert('FAQ added successfully!');
            } else if (isEditingFAQ && selectedFAQ) {
                // Include status: 'answered' when saving an answer
                const payload = {
                    ...faqFormData,
                    status: faqFormData.answer && faqFormData.answer.trim() !== '' ? 'answered' : 'pending'
                };
                await updateFAQ(selectedFAQ._id, payload);
                alert('FAQ updated successfully!');
            }
            const updated = await getFAQs();
            if (Array.isArray(updated)) {
                setFaqs(updated.reverse());
            }
            resetFAQForm();
        } catch (error) {
            console.error('Error saving FAQ:', error);
            alert('Error saving FAQ: ' + (error.message || 'Unknown error'));
        } finally {
            setIsSavingFAQ(false);
        }
    };

    // What's New Handlers
    const resetWhatsNewForm = () => {
        setVideoPreviewError(false);
        setWhatsNewDescriptionError(false);
        setWhatsNewFormData({
            title: '',
            category: selectedWhatsNewCategory,
            type: '',
            platform: '',
            date: getCurrentFormattedDate(),
            actions: '',
            description: '',
            media: '',
            mediaType: 'none',
            version: ''
        });
        setSelectedWhatsNew(null);
        setIsAddingWhatsNew(false);
        setIsEditingWhatsNew(false);
        setIsCustomType(false);
        setCustomUpdateType('');
        setIsCustomPlatform(false);
        setCustomPlatform('');
        setIsSavingWhatsNew(false);
    };

    const handleWhatsNewInputChange = (e) => {
        const { name, value } = e.target;
        setWhatsNewFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleWhatsNewDescriptionChange = (content) => {
        if (whatsNewDescriptionError) setWhatsNewDescriptionError(false);
        setWhatsNewFormData(prev => ({
            ...prev,
            description: content
        }));
    };

    const handleWhatsNewActionsChange = (content) => {
        setWhatsNewFormData(prev => ({
            ...prev,
            actions: content
        }));
    };

    const handleWhatsNewMediaUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        if (file.type.startsWith('image/')) {
            try {
                const compressed = await compressImageFileOrDataUrl(file);
                setWhatsNewFormData(prev => ({
                    ...prev,
                    media: compressed,
                    mediaType: 'image'
                }));
            } catch (err) {
                const reader = new FileReader();
                reader.onload = (loadEvent) => {
                    setWhatsNewFormData(prev => ({
                        ...prev,
                        media: loadEvent.target.result,
                        mediaType: 'image'
                    }));
                };
                reader.readAsDataURL(file);
            }
        } else if (file.type.startsWith('video/') || /\.(mp4|webm|mov|ogg|m4v|mkv)$/i.test(file.name || '')) {
            const reader = new FileReader();
            reader.onload = (loadEvent) => {
                setWhatsNewFormData(prev => ({
                    ...prev,
                    media: loadEvent.target.result,
                    mediaType: 'video'
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleTypeSelectChange = (e) => {
        const val = e.target.value;
        if (val === 'Other') {
            setIsCustomType(true);
            setWhatsNewFormData(prev => ({
                ...prev,
                type: customUpdateType || 'Other'
            }));
        } else {
            setIsCustomType(false);
            setWhatsNewFormData(prev => ({
                ...prev,
                type: val
            }));
        }
    };

    const handleCustomTypeInputChange = (e) => {
        const val = e.target.value;
        setCustomUpdateType(val);
        setWhatsNewFormData(prev => ({
            ...prev,
            type: val
        }));
    };

    const handlePlatformSelectChange = (e) => {
        const val = e.target.value;
        if (val === 'Other') {
            setIsCustomPlatform(true);
            setWhatsNewFormData(prev => ({
                ...prev,
                platform: customPlatform || 'Other'
            }));
        } else {
            setIsCustomPlatform(false);
            setWhatsNewFormData(prev => ({
                ...prev,
                platform: val
            }));
        }
    };

    const handleCustomPlatformInputChange = (e) => {
        const val = e.target.value;
        setCustomPlatform(val);
        setWhatsNewFormData(prev => ({
            ...prev,
            platform: val
        }));
    };

    const handleAddWhatsNewClick = () => {
        setWhatsNewDescriptionError(false);
        setWhatsNewFormData({
            title: '',
            category: selectedWhatsNewCategory,
            type: '',
            platform: '',
            date: getCurrentFormattedDate(),
            actions: '',
            description: '',
            media: '',
            mediaType: 'none',
            version: ''
        });
        setSelectedWhatsNew(null);
        setIsAddingWhatsNew(true);
        setIsEditingWhatsNew(false);
        setIsCustomType(false);
        setCustomUpdateType('');
        setIsCustomPlatform(false);
        setCustomPlatform('');
    };

    const handleEditWhatsNewClick = (item) => {
        setWhatsNewDescriptionError(false);
        const PREDEFINED_TYPES = ['New Feature', 'Improvement', 'Design', 'Bug Fix', 'Announcement'];
        const rawType = item.type || '';
        const isCustom = rawType && !PREDEFINED_TYPES.includes(rawType);
        setIsCustomType(Boolean(isCustom));
        setCustomUpdateType(isCustom ? rawType : '');

        const PREDEFINED_PLATFORMS = ['Web', 'Mobile', 'Web & Mobile', 'Desktop', 'iOS & Android'];
        const rawPlatform = item.platform || '';
        const isCustomPlat = rawPlatform && !PREDEFINED_PLATFORMS.includes(rawPlatform);
        setIsCustomPlatform(Boolean(isCustomPlat));
        setCustomPlatform(isCustomPlat ? rawPlatform : '');

        setSelectedWhatsNew(item);
        setVideoPreviewError(false);
        setWhatsNewFormData({
            title: item.title || '',
            category: item.category || selectedWhatsNewCategory,
            type: rawType,
            platform: rawPlatform,
            date: item.date || getCurrentFormattedDate(),
            actions: item.actions || '',
            description: item.description || '',
            media: item.media || '',
            mediaType: item.mediaType || (item.media ? 'image' : 'none'),
            version: item.version || ''
        });
        setIsEditingWhatsNew(true);
        setIsAddingWhatsNew(false);
    };

    const handleSaveWhatsNew = async (e) => {
        if (e) e.preventDefault();
        if (isSavingWhatsNew) return;

        if (!whatsNewFormData.title || !whatsNewFormData.title.trim()) {
            alert('Please enter a feature title');
            return;
        }

        const rawDesc = whatsNewFormData.description || '';
        const strippedDescription = rawDesc
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .trim();
        const hasMediaTag = /<img|<svg|<video|<iframe/i.test(rawDesc);
        if (!strippedDescription && !hasMediaTag) {
            setWhatsNewDescriptionError(true);
            alert('Short Description is compulsory. Please enter a short description.');
            return;
        }

        setIsSavingWhatsNew(true);
        try {
            const finalType = isCustomType ? (customUpdateType.trim() || 'Other') : (whatsNewFormData.type || 'New Feature');
            const finalPlatform = isCustomPlatform ? (customPlatform.trim() || 'Other') : (whatsNewFormData.platform || 'Web');
            const cleanMedia = (whatsNewFormData.media || '').trim();
            const finalMediaType = cleanMedia ? (whatsNewFormData.mediaType === 'video' || isWhatsNewVideo(cleanMedia) ? 'video' : 'image') : 'none';
            const payload = {
                ...whatsNewFormData,
                media: cleanMedia,
                mediaType: finalMediaType,
                type: finalType,
                platform: finalPlatform
            };
            const targetId = selectedWhatsNew ? (selectedWhatsNew._id || selectedWhatsNew.id) : null;
            if (isAddingWhatsNew) {
                await addWhatsNew(payload);
                alert("What's New item added successfully!");
            } else if (isEditingWhatsNew && targetId) {
                await updateWhatsNew(targetId, payload);
                alert("What's New item updated successfully!");
            }
            const updated = await getWhatsNew();
            if (Array.isArray(updated)) {
                setWhatsNewList(updated);
            }
            resetWhatsNewForm();
        } catch (error) {
            console.error('Error saving What\'s New item:', error);
            alert('Error saving What\'s New item: ' + (error.message || 'Unknown error'));
        } finally {
            setIsSavingWhatsNew(false);
        }
    };

    const handleDeleteWhatsNew = async (id) => {
        if (window.confirm("Are you sure you want to delete this What's New item?")) {
            await deleteWhatsNew(id);
            setSelectedWhatsNewIds(prev => prev.filter(itemId => String(itemId) !== String(id)));
            const updated = await getWhatsNew();
            setWhatsNewList(updated);
        }
    };

    // What's New Bulk Selection Handlers
    const handleToggleSelectWhatsNew = (id) => {
        if (!id) return;
        const idStr = String(id);
        setSelectedWhatsNewIds(prev =>
            prev.map(String).includes(idStr)
                ? prev.filter(item => String(item) !== idStr)
                : [...prev, id]
        );
    };

    const handleToggleSelectAllWhatsNew = () => {
        const currentCategoryItems = whatsNewList.filter(item => item.category === selectedWhatsNewCategory);
        const allIds = currentCategoryItems.map(item => item._id || item.id).filter(Boolean);
        if (allIds.length === 0) return;
        const allIdsStr = allIds.map(String);
        const selectedStr = selectedWhatsNewIds.map(String);
        const isAllSelected = allIdsStr.every(id => selectedStr.includes(id));
        if (isAllSelected) {
            setSelectedWhatsNewIds(prev => prev.filter(id => !allIdsStr.includes(String(id))));
        } else {
            setSelectedWhatsNewIds(prev => Array.from(new Set([...prev.map(String), ...allIdsStr])));
        }
    };

    const handleBulkDeleteWhatsNew = async () => {
        if (selectedWhatsNewIds.length === 0 || isBulkDeleting) return;
        const count = selectedWhatsNewIds.length;
        if (!window.confirm(`Are you sure you want to delete ${count} selected What's New item(s)? This action cannot be undone.`)) {
            return;
        }
        setIsBulkDeleting(true);
        try {
            await Promise.all(
                selectedWhatsNewIds.map(id =>
                    deleteWhatsNew(id).catch(err => {
                        console.error(`Failed to delete What's New item ${id}:`, err);
                        return null;
                    })
                )
            );
            setSelectedWhatsNewIds([]);
            const updated = await getWhatsNew();
            setWhatsNewList(updated);
            alert(`Successfully deleted ${count} What's New update(s).`);
        } catch (error) {
            console.error('Error bulk deleting What\'s New items:', error);
            alert('An error occurred during bulk deletion.');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // If not authenticated, show login screen
    if (!isAuthenticated) {
        return (
            <div className="blog-admin-wrapper">
                <div className="login-container">
                    <div className="login-card">
                        <div className="login-header">
                            <div className="login-icon">🔐</div>
                            <h1>Admin Login</h1>
                            <p>Sign in to manage content</p>
                        </div>
                        <form className="login-form" onSubmit={handleLogin}>
                            <div className="login-form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={loginData.email}
                                    onChange={handleLoginInputChange}
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>
                            <div className="login-form-group">
                                <label>Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showLoginPassword ? "text" : "password"}
                                        name="password"
                                        value={loginData.password}
                                        onChange={handleLoginInputChange}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                                        tabIndex={-1}
                                    >
                                        {showLoginPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>
                            <div className="remember-me-group">
                                <label className="remember-me-label">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="remember-me-checkbox"
                                    />
                                    <span className="checkmark"></span>
                                    Remember me
                                </label>
                            </div>
                            {loginError && (
                                <div className="login-error">
                                    <span>⚠️</span> {loginError}
                                </div>
                            )}
                            <button
                                type="submit"
                                className="login-submit-btn"
                                disabled={isLoggingIn}
                            >
                                {isLoggingIn ? 'Signing in...' : 'Sign In'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Authenticated: Show admin dashboard
    // Filtered lists and bulk selection helper states
    const currentCategoryBlogs = blogs.filter(blog => blog.category === selectedCategory);
    const blogIdsInCurrentCategory = currentCategoryBlogs.map(b => b._id).filter(Boolean);
    const isAllBlogsSelected = blogIdsInCurrentCategory.length > 0 && blogIdsInCurrentCategory.every(id => selectedBlogIds.includes(id));
    const isSomeBlogsSelected = blogIdsInCurrentCategory.some(id => selectedBlogIds.includes(id));

    const currentCategoryFaqs = faqs.filter(f => f.category === selectedFAQCategory);
    const faqIdsInCurrentCategory = currentCategoryFaqs.map(f => f._id).filter(Boolean);
    const isAllFaqsSelected = faqIdsInCurrentCategory.length > 0 && faqIdsInCurrentCategory.every(id => selectedFaqIds.includes(id));
    const isSomeFaqsSelected = faqIdsInCurrentCategory.some(id => selectedFaqIds.includes(id));

    const currentCategoryWhatsNew = whatsNewList.filter(item => item.category === selectedWhatsNewCategory);
    const whatsNewIdsInCurrentCategory = currentCategoryWhatsNew.map(item => String(item._id || item.id)).filter(Boolean);
    const selectedWhatsNewStr = selectedWhatsNewIds.map(String);
    const isAllWhatsNewSelected = whatsNewIdsInCurrentCategory.length > 0 && whatsNewIdsInCurrentCategory.every(id => selectedWhatsNewStr.includes(id));
    const isSomeWhatsNewSelected = whatsNewIdsInCurrentCategory.some(id => selectedWhatsNewStr.includes(id));

    return (

        <div className="blog-admin-wrapper">
            {/* Header */}
            <header className="admin-header">
                <div className="admin-header-content">
                    <h1>Admin Dashboard</h1>

                </div>
                <div className="header-buttons">
                    <button className={`nav-btn ${view === 'blogs' ? 'active' : ''}`} onClick={() => { setView('blogs'); setSelectedBlogIds([]); }}>Blogs</button>
                    <button className={`nav-btn ${view === 'faqs' ? 'active' : ''}`} onClick={() => { setView('faqs'); setSelectedFaqIds([]); }}>FAQs</button>
                    <button className={`nav-btn ${view === 'whatsNew' ? 'active' : ''}`} onClick={() => { setView('whatsNew'); setSelectedWhatsNewIds([]); }}>What's new?</button>
                    <button className="nav-btn" onClick={() => setShowChangePassword(true)}>Change Password</button>

                    <button className="logout-btn" onClick={handleLogout}>
                        Logout
                    </button>
                    {localStorage.getItem('adminAuth') && (
                        <button className="forget-me-btn" onClick={handleForgetMe} title="Clear saved credentials">
                            Forget me
                        </button>
                    )}
                </div>
            </header>

            {/* Change Password Modal */}
            {showChangePassword && (
                <div className="modal-overlay">
                    <div className="blog-modal-content password-modal">
                        <div className="modal-header">
                            <h2>Change Password</h2>
                            <button className="close-btn" onClick={resetPasswordForm}>×</button>
                        </div>
                        <form className="blog-form" onSubmit={handleChangePassword}>
                            <div className="form-group">
                                <label>Current Password *</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        name="currentPassword"
                                        value={passwordFormData.currentPassword}
                                        onChange={handlePasswordInputChange}
                                        placeholder="Enter your current password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        tabIndex={-1}
                                    >
                                        {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>New Password *</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={passwordFormData.newPassword}
                                        onChange={handlePasswordInputChange}
                                        placeholder="Enter new password (min 6 characters)"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        tabIndex={-1}
                                    >
                                        {showNewPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Re-type New Password *</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={passwordFormData.confirmPassword}
                                        onChange={handlePasswordInputChange}
                                        placeholder="Confirm new password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex={-1}
                                    >
                                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>
                            {passwordError && (
                                <div className="password-error">
                                    <span>⚠️</span> {passwordError}
                                </div>
                            )}
                            {passwordSuccess && (
                                <div className="password-success">
                                    <span>✅</span> {passwordSuccess}
                                </div>
                            )}
                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={resetPasswordForm}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={isChangingPassword}>
                                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {view === 'blogs' && (
                /* Main Content - BLOGS */
                <div className="admin-container">
                    {/* Filter Tabs */}
                    <div className="admin-tabs">
                        <button
                            className={`organiser-tab ${selectedCategory === 'Organiser' ? 'active' : ''}`}
                            onClick={() => { setSelectedCategory('Organiser'); setSelectedBlogIds([]); }}
                        >
                            <span>Organiser</span>
                        </button>
                        <button
                            className={`individual-tab ${selectedCategory === 'Individual' ? 'active' : ''}`}
                            onClick={() => { setSelectedCategory('Individual'); setSelectedBlogIds([]); }}
                        >
                            <span>Individual</span>
                        </button>
                    </div>

                    {/* Actions Bar */}
                    <div className="admin-actions">
                        <div className="admin-actions-left">
                            <button
                                className="add-btn"
                                onClick={handleAddBlogClick}
                            >
                                + Add New Blog
                            </button>

                            {currentCategoryBlogs.length > 0 && (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontSize: '14px', color: '#475569', fontWeight: 500, background: '#fff', padding: '10px 16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
                                    <input
                                        type="checkbox"
                                        className="admin-checkbox"
                                        checked={isAllBlogsSelected}
                                        ref={el => { if (el) el.indeterminate = isSomeBlogsSelected && !isAllBlogsSelected; }}
                                        onChange={handleToggleSelectAllBlogs}
                                        disabled={isBulkDeleting}
                                    />
                                    <span>Select All</span>
                                </label>
                            )}

                            {selectedBlogIds.length > 0 && (
                                <div className="bulk-actions-group">
                                    <button
                                        type="button"
                                        className="bulk-delete-btn"
                                        onClick={handleBulkDeleteBlogs}
                                        disabled={isBulkDeleting}
                                    >
                                        {isBulkDeleting ? (
                                            <span className="btn-loading-content">
                                                <span className="btn-spinner"></span>
                                                <span>Deleting...</span>
                                            </span>
                                        ) : (
                                            `🗑️ Delete Selected (${selectedBlogIds.length})`
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="clear-selection-btn"
                                        onClick={() => setSelectedBlogIds([])}
                                        disabled={isBulkDeleting}
                                    >
                                        Deselect All
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="admin-actions-right">
                            <span className="blog-count">{currentCategoryBlogs.length} blogs</span>
                        </div>
                    </div>

                    {/* Add/Edit Form Modal */}
                    {(isAdding || isEditing) && (
                        <div className="modal-overlay">
                            <div className="blog-modal-content">
                                <div className="modal-header">
                                    <h2>{isAdding ? 'Add New Blog' : 'Edit Blog'}</h2>
                                    <button className="close-btn" onClick={resetForm} disabled={isSavingBlog}>×</button>
                                </div>
                                <form className="blog-form" onSubmit={(e) => {
                                    e.preventDefault();
                                    isAdding ? handleAddBlog() : handleSaveEdit();
                                }}>
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter blog title"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <div className="category-selection">
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="category"
                                                    value="Individual"
                                                    checked={formData.category === 'Individual'}
                                                    onChange={handleInputChange}
                                                />
                                                <span>Individual</span>
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="category"
                                                    value="Organiser"
                                                    checked={formData.category === 'Organiser'}
                                                    onChange={handleInputChange}
                                                />
                                                <span>Organiser</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Date *</label>
                                            <input
                                                type="text"
                                                name="date"
                                                value={formData.date}
                                                onChange={handleInputChange}
                                                placeholder="e.g., January 8, 2026"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Summary *</label>
                                        <textarea
                                            name="summary"
                                            value={formData.summary}
                                            onChange={handleInputChange}
                                            placeholder="Brief summary of the blog post"
                                            rows="2"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Blog Cover Image</label>
                                        <div className="image-upload-container">
                                            <input
                                                type="file"
                                                id="blog-image-input"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="file-input"
                                            />
                                            {formData.image && formData.image.trim() !== '' && formData.image !== 'NONE' && formData.image !== 'NO_IMAGE' && formData.image !== BlogImage && !formData.image.includes('UExBQ0VIT0xERVJfSU1BR0') ? (
                                                <div className="image-preview-wrapper">
                                                    <div className="image-preview" title="Cover Image Preview">
                                                        <img src={formData.image} alt="Cover Preview" />
                                                    </div>
                                                    <div className="image-controls">
                                                        <label htmlFor="blog-image-input" className="file-input-label">
                                                            Change Image
                                                        </label>
                                                        <button
                                                            type="button"
                                                            className="remove-image-btn"
                                                            onClick={handleRemoveImage}
                                                            title="Remove cover image"
                                                        >
                                                            Remove Image
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="image-controls">
                                                    <label htmlFor="blog-image-input" className="file-input-label upload-box-btn">
                                                        Choose Image
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                                            <label style={{ margin: 0 }}>Content *</label>
                                            <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                                                🖼️ Multiple images can be inserted anywhere in the text using the image icon on the toolbar, or by pasting / dragging images!
                                            </span>
                                        </div>
                                        <RichTextEditor
                                            value={formData.content}
                                            onChange={handleContentChange}
                                            placeholder="Write your blog content here... You can format text and insert multiple images anywhere at the cursor position."
                                        />
                                    </div>

                                    {/* Search & SEO Metadata (Hidden from public users) */}
                                    <div className="seo-metadata-card">
                                        <div className="seo-card-header">
                                            <div className="seo-card-title-group">
                                                <div className="seo-badge-tag">SEARCH &amp; SEO METADATA</div>
                                                <span className="seo-private-notice">
                                                    (Internal search and ranking only — won't be shown to users)
                                                </span>
                                            </div>
                                        </div>

                                        {/* Tags */}
                                        <div className="seo-field-group">
                                            <label className="seo-field-label">TAGS</label>
                                            <div className="seo-chips-container">
                                                {(Array.isArray(formData.tags) ? formData.tags : parseChips(formData.tags)).map((tag, idx) => (
                                                    <span key={idx} className="seo-chip tag-chip">
                                                        <span className="seo-chip-text">{tag}</span>
                                                        <button
                                                            type="button"
                                                            className="seo-chip-remove"
                                                            onClick={() => handleRemoveTag(idx)}
                                                            title={`Remove ${tag}`}
                                                            aria-label={`Remove ${tag}`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                                {isAddingTag ? (
                                                    <div className="seo-chip-input-wrapper">
                                                        <input
                                                            ref={tagInputRef}
                                                            type="text"
                                                            className="seo-chip-input"
                                                            value={tagInputValue}
                                                            onChange={(e) => setTagInputValue(e.target.value)}
                                                            onKeyDown={handleTagKeyDown}
                                                            onBlur={() => {
                                                                if (tagInputValue.trim()) {
                                                                    handleAddTag();
                                                                }
                                                                setIsAddingTag(false);
                                                            }}
                                                            placeholder="Type tag & Enter"
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            className="seo-chip-add-action"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={handleAddTag}
                                                            title="Add tag"
                                                        >
                                                            ✓
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="seo-add-chip-btn"
                                                        onClick={() => {
                                                            setIsAddingTag(true);
                                                            setTimeout(() => tagInputRef.current?.focus(), 50);
                                                        }}
                                                    >
                                                        + Add tag
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Keywords */}
                                        <div className="seo-keywords-section">
                                            <div className="seo-keywords-heading">KEYWORDS</div>

                                            {/* Primary Target Keyword */}
                                            <div className="seo-field-group" style={{ marginBottom: '18px' }}>
                                                <label className="seo-subfield-label">
                                                    Primary Target Keyword
                                                </label>
                                                <div className="seo-chips-container">
                                                    {(Array.isArray(formData.primaryKeyword) ? formData.primaryKeyword : parseChips(formData.primaryKeyword)).map((keyword, idx) => (
                                                        <span key={idx} className="seo-chip keyword-chip primary-chip">
                                                            <span className="seo-chip-text">{keyword}</span>
                                                            <button
                                                                type="button"
                                                                className="seo-chip-remove"
                                                                onClick={() => handleRemovePrimaryKeyword(idx)}
                                                                title={`Remove ${keyword}`}
                                                                aria-label="Remove primary keyword"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                    {isAddingPrimaryKeyword ? (
                                                        <div className="seo-chip-input-wrapper">
                                                            <input
                                                                ref={primaryKeywordInputRef}
                                                                type="text"
                                                                className="seo-chip-input"
                                                                value={primaryKeywordInputValue}
                                                                onChange={(e) => setPrimaryKeywordInputValue(e.target.value)}
                                                                onKeyDown={handlePrimaryKeywordKeyDown}
                                                                onBlur={() => {
                                                                    if (primaryKeywordInputValue.trim()) {
                                                                        handleAddPrimaryKeyword();
                                                                    }
                                                                    setIsAddingPrimaryKeyword(false);
                                                                }}
                                                                placeholder="Type keyword & Enter"
                                                                autoFocus
                                                            />
                                                            <button
                                                                type="button"
                                                                className="seo-chip-add-action"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={handleAddPrimaryKeyword}
                                                                title="Add primary keyword"
                                                            >
                                                                ✓
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="seo-add-chip-btn"
                                                            onClick={() => {
                                                                setIsAddingPrimaryKeyword(true);
                                                                setTimeout(() => primaryKeywordInputRef.current?.focus(), 50);
                                                            }}
                                                        >
                                                            + Add keyword
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Secondary Semantic Keywords */}
                                            <div className="seo-field-group" style={{ marginBottom: 0 }}>
                                                <label className="seo-subfield-label">
                                                    Secondary Semantic Keywords
                                                </label>
                                                <div className="seo-chips-container">
                                                    {(Array.isArray(formData.secondaryKeywords) ? formData.secondaryKeywords : parseChips(formData.secondaryKeywords)).map((keyword, idx) => (
                                                        <span key={idx} className="seo-chip keyword-chip">
                                                            <span className="seo-chip-text">{keyword}</span>
                                                            <button
                                                                type="button"
                                                                className="seo-chip-remove"
                                                                onClick={() => handleRemoveKeyword(idx)}
                                                                title={`Remove ${keyword}`}
                                                                aria-label={`Remove ${keyword}`}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                    {isAddingKeyword ? (
                                                        <div className="seo-chip-input-wrapper">
                                                            <input
                                                                ref={keywordInputRef}
                                                                type="text"
                                                                className="seo-chip-input"
                                                                value={keywordInputValue}
                                                                onChange={(e) => setKeywordInputValue(e.target.value)}
                                                                onKeyDown={handleKeywordKeyDown}
                                                                onBlur={() => {
                                                                    if (keywordInputValue.trim()) {
                                                                        handleAddKeyword();
                                                                    }
                                                                    setIsAddingKeyword(false);
                                                                }}
                                                                placeholder="Type keyword & Enter"
                                                                autoFocus
                                                            />
                                                            <button
                                                                type="button"
                                                                className="seo-chip-add-action"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={handleAddKeyword}
                                                                title="Add keyword"
                                                            >
                                                                ✓
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="seo-add-chip-btn"
                                                            onClick={() => {
                                                                setIsAddingKeyword(true);
                                                                setTimeout(() => keywordInputRef.current?.focus(), 50);
                                                            }}
                                                        >
                                                            + Add keyword
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-actions">
                                        <button type="button" className="cancel-btn" onClick={resetForm} disabled={isSavingBlog}>Cancel</button>
                                        <button type="submit" className="submit-btn" disabled={isSavingBlog}>
                                            {isSavingBlog ? (
                                                <span className="btn-loading-content">
                                                    <span className="btn-spinner"></span>
                                                    <span>{isAdding ? 'Adding Blog...' : 'Saving Changes...'}</span>
                                                </span>
                                            ) : (
                                                isAdding ? 'Add Blog' : 'Save Changes'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteConfirm && (
                        <div className="modal-overlay">
                            <div className="blog-modal-content delete-modal">
                                <div className="delete-icon">🗑️</div>
                                <h3>Delete Blog Post?</h3>
                                <p>Are you sure you want to delete "<strong>{selectedBlog?.title}</strong>"?</p>
                                <p className="warning-text">This action cannot be undone.</p>
                                <div className="form-actions">
                                    <button className="cancel-btn" onClick={resetForm} disabled={isDeletingBlog}>Cancel</button>
                                    <button className="delete-confirm-btn" onClick={confirmDelete} disabled={isDeletingBlog}>
                                        {isDeletingBlog ? (
                                            <span className="btn-loading-content">
                                                <span className="btn-spinner"></span>
                                                <span>Deleting...</span>
                                            </span>
                                        ) : (
                                            'Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Blog Table */}
                    <div className="blog-table-container">
                        <table className="blog-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '45px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            className="admin-checkbox"
                                            checked={isAllBlogsSelected}
                                            ref={el => { if (el) el.indeterminate = isSomeBlogsSelected && !isAllBlogsSelected; }}
                                            onChange={handleToggleSelectAllBlogs}
                                            disabled={isBulkDeleting || currentCategoryBlogs.length === 0}
                                            title="Select all in this category"
                                        />
                                    </th>
                                    <th>ID</th>
                                    <th>Title</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCategoryBlogs.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                            No blogs found for {selectedCategory}. Click "+ Add New Blog" to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    currentCategoryBlogs.map((blog) => {
                                        const isSelected = selectedBlogIds.includes(blog._id);
                                        return (
                                            <tr key={blog._id || Math.random()} className={isSelected ? 'selected-row' : ''}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="admin-checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleToggleSelectBlog(blog._id)}
                                                        disabled={isBulkDeleting}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className="id-cell">{(blog._id || 'N/A').substring(0, 8)}...</td>
                                                <td className="title-cell">
                                                    <div className="title-wrapper">
                                                        {blog.image && blog.image.trim() !== '' && blog.image !== 'NONE' && blog.image !== 'NO_IMAGE' && blog.image !== BlogImage && !blog.image.includes('UExBQ0VIT0xERVJfSU1BR0') && (
                                                            <img src={blog.image} alt="" className="blog-thumbnail" />
                                                        )}
                                                        <span>{blog.title}</span>
                                                    </div>
                                                </td>
                                                <td className="date-cell">{blog.date}</td>
                                                <td className="actions-cell">
                                                    <button
                                                        className="action-btn view-btn"
                                                        onClick={() => handleViewBlog(blog._id)}
                                                        title="View"
                                                    >
                                                        👁️
                                                    </button>
                                                    <button
                                                        className="action-btn edit-btn"
                                                        onClick={() => handleEditClick(blog)}
                                                        title="Edit"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteClick(blog)}
                                                        title="Delete"
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {view === 'faqs' && (
                /* Main Content - FAQS */
                <div className="admin-container">
                    {/* FAQ Filter Tabs */}
                    <div className="admin-tabs">
                        <button
                            className={`organiser-tab ${selectedFAQCategory === 'Organiser' ? 'active' : ''}`}
                            onClick={() => { setSelectedFAQCategory('Organiser'); setSelectedFaqIds([]); }}
                        >
                            <span>Organiser</span>
                        </button>
                        <button
                            className={`individual-tab ${selectedFAQCategory === 'Individual' ? 'active' : ''}`}
                            onClick={() => { setSelectedFAQCategory('Individual'); setSelectedFaqIds([]); }}
                        >
                            <span>Individual</span>
                        </button>
                    </div>

                    <div className="admin-actions">
                        <div className="admin-actions-left">
                            <button
                                className="add-btn"
                                onClick={handleAddFAQClick}
                            >
                                + Add New FAQ
                            </button>

                            {currentCategoryFaqs.length > 0 && (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontSize: '14px', color: '#475569', fontWeight: 500, background: '#fff', padding: '10px 16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
                                    <input
                                        type="checkbox"
                                        className="admin-checkbox"
                                        checked={isAllFaqsSelected}
                                        ref={el => { if (el) el.indeterminate = isSomeFaqsSelected && !isAllFaqsSelected; }}
                                        onChange={handleToggleSelectAllFaqs}
                                        disabled={isBulkDeleting}
                                    />
                                    <span>Select All</span>
                                </label>
                            )}

                            {selectedFaqIds.length > 0 && (
                                <div className="bulk-actions-group">
                                    <button
                                        type="button"
                                        className="bulk-delete-btn"
                                        onClick={handleBulkDeleteFaqs}
                                        disabled={isBulkDeleting}
                                    >
                                        {isBulkDeleting ? (
                                            <span className="btn-loading-content">
                                                <span className="btn-spinner"></span>
                                                <span>Deleting...</span>
                                            </span>
                                        ) : (
                                            `🗑️ Delete Selected (${selectedFaqIds.length})`
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="clear-selection-btn"
                                        onClick={() => setSelectedFaqIds([])}
                                        disabled={isBulkDeleting}
                                    >
                                        Deselect All
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="admin-actions-right">
                            <span className="blog-count">{currentCategoryFaqs.length} FAQs</span>
                        </div>
                    </div>

                    {/* FAQ Table */}
                    <div className="blog-table-container">
                        <table className="blog-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '45px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            className="admin-checkbox"
                                            checked={isAllFaqsSelected}
                                            ref={el => { if (el) el.indeterminate = isSomeFaqsSelected && !isAllFaqsSelected; }}
                                            onChange={handleToggleSelectAllFaqs}
                                            disabled={isBulkDeleting || currentCategoryFaqs.length === 0}
                                            title="Select all in this category"
                                        />
                                    </th>
                                    <th>Question</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCategoryFaqs.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                            No FAQs found for {selectedFAQCategory}. Click "+ Add New FAQ" to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    currentCategoryFaqs.map((faq) => {
                                        const isSelected = selectedFaqIds.includes(faq._id);
                                        return (
                                            <tr key={faq._id || Math.random()} className={isSelected ? 'selected-row' : ''}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="admin-checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleToggleSelectFaq(faq._id)}
                                                        disabled={isBulkDeleting}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className="title-cell" style={{ maxWidth: '400px' }}>
                                                    <span>{faq.question}</span>
                                                </td>
                                                <td>{faq.category}</td>
                                                <td>
                                                    <span className={`status-badge ${faq.status || 'pending'}`}>
                                                        {faq.status || 'pending'}
                                                    </span>
                                                </td>
                                                <td className="actions-cell">
                                                    <button
                                                        className="action-btn edit-btn"
                                                        onClick={() => handleEditFAQClick(faq)}
                                                        title="Edit/Answer"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteFAQ(faq._id)}
                                                        title="Delete"
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Add/Edit FAQ Modal */}
                    {(isAddingFAQ || isEditingFAQ) && (
                        <div className="modal-overlay">
                            <div className="blog-modal-content">
                                <div className="modal-header">
                                    <h2>{isAddingFAQ ? 'Add New FAQ' : 'Edit FAQ'}</h2>
                                    <button className="close-btn" onClick={resetFAQForm} disabled={isSavingFAQ}>×</button>
                                </div>
                                <form className="blog-form" onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSaveFAQ();
                                }}>
                                    <div className="form-group">
                                        <label>Question *</label>
                                        <input
                                            type="text"
                                            name="question"
                                            value={faqFormData.question}
                                            onChange={handleFAQInputChange}
                                            placeholder="Enter the FAQ question"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <div className="category-selection">
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="category"
                                                    value="Individual"
                                                    checked={faqFormData.category === 'Individual'}
                                                    onChange={handleFAQInputChange}
                                                />
                                                <span>Individual</span>
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="category"
                                                    value="Organiser"
                                                    checked={faqFormData.category === 'Organiser'}
                                                    onChange={handleFAQInputChange}
                                                />
                                                <span>Organiser</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Answer *</label>
                                        <RichTextEditor
                                            value={faqFormData.answer}
                                            onChange={handleFAQContentChange}
                                            placeholder="Write the answer here..."
                                        />
                                    </div>
                                    <div className="form-actions">
                                        <button type="button" className="cancel-btn" onClick={resetFAQForm} disabled={isSavingFAQ}>Cancel</button>
                                        <button type="submit" className="submit-btn" disabled={isSavingFAQ}>
                                            {isSavingFAQ ? (
                                                <span className="btn-loading-content">
                                                    <span className="btn-spinner"></span>
                                                    <span>{isAddingFAQ ? 'Adding FAQ...' : 'Saving FAQ...'}</span>
                                                </span>
                                            ) : (
                                                isAddingFAQ ? 'Add FAQ' : 'Save FAQ'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {view === 'whatsNew' && (
                /* Main Content - WHAT'S NEW */
                <div className="admin-container">
                    {/* What's New Filter Tabs */}
                    <div className="admin-tabs">
                        <button
                            className={`organiser-tab ${selectedWhatsNewCategory === 'Organiser' ? 'active' : ''}`}
                            onClick={() => { setSelectedWhatsNewCategory('Organiser'); setSelectedWhatsNewIds([]); }}
                        >
                            <span>Organiser</span>
                        </button>
                        <button
                            className={`individual-tab ${selectedWhatsNewCategory === 'Individual' ? 'active' : ''}`}
                            onClick={() => { setSelectedWhatsNewCategory('Individual'); setSelectedWhatsNewIds([]); }}
                        >
                            <span>Individual</span>
                        </button>
                    </div>

                    <div className="admin-actions">
                        <div className="admin-actions-left">
                            <button
                                className="add-btn"
                                onClick={handleAddWhatsNewClick}
                            >
                                + Add What's New
                            </button>

                            {currentCategoryWhatsNew.length > 0 && (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontSize: '14px', color: '#475569', fontWeight: 500, background: '#fff', padding: '10px 16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
                                    <input
                                        type="checkbox"
                                        className="admin-checkbox"
                                        checked={isAllWhatsNewSelected}
                                        ref={el => { if (el) el.indeterminate = isSomeWhatsNewSelected && !isAllWhatsNewSelected; }}
                                        onChange={handleToggleSelectAllWhatsNew}
                                        disabled={isBulkDeleting}
                                    />
                                    <span>Select All</span>
                                </label>
                            )}

                            {selectedWhatsNewIds.length > 0 && (
                                <div className="bulk-actions-group">
                                    <button
                                        type="button"
                                        className="bulk-delete-btn"
                                        onClick={handleBulkDeleteWhatsNew}
                                        disabled={isBulkDeleting}
                                    >
                                        {isBulkDeleting ? (
                                            <span className="btn-loading-content">
                                                <span className="btn-spinner"></span>
                                                <span>Deleting...</span>
                                            </span>
                                        ) : (
                                            `🗑️ Delete Selected (${selectedWhatsNewIds.length})`
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="clear-selection-btn"
                                        onClick={() => setSelectedWhatsNewIds([])}
                                        disabled={isBulkDeleting}
                                    >
                                        Deselect All
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="admin-actions-right">
                            <span className="blog-count">
                                {currentCategoryWhatsNew.length} updates
                            </span>
                        </div>
                    </div>

                    {/* What's New Table */}
                    <div className="blog-table-container">
                        <table className="blog-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '45px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            className="admin-checkbox"
                                            checked={isAllWhatsNewSelected}
                                            ref={el => { if (el) el.indeterminate = isSomeWhatsNewSelected && !isAllWhatsNewSelected; }}
                                            onChange={handleToggleSelectAllWhatsNew}
                                            disabled={isBulkDeleting || currentCategoryWhatsNew.length === 0}
                                            title="Select all in this category"
                                        />
                                    </th>
                                    <th>Feature & Description</th>
                                    <th style={{ width: '160px' }}>Metadata</th>
                                    <th style={{ width: '140px' }}>Date</th>
                                    <th style={{ width: '80px' }}>Media</th>
                                    <th style={{ width: '100px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCategoryWhatsNew.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                            No What's New items found for {selectedWhatsNewCategory}. Click "+ Add What's New" to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    currentCategoryWhatsNew
                                        .map((item) => {
                                            const itemId = item.id || item._id;
                                            const isSelected = selectedWhatsNewIds.map(String).includes(String(itemId));
                                            return (
                                                <tr key={itemId || Math.random()} className={isSelected ? 'selected-row' : ''}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="admin-checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleToggleSelectWhatsNew(itemId)}
                                                            disabled={isBulkDeleting}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td className="title-cell" style={{ maxWidth: '420px' }}>
                                                        <div style={{ fontWeight: 600, color: '#1a202c', fontSize: '15px', marginBottom: '4px' }}>
                                                            {item.title || 'Untitled Update'}
                                                        </div>
                                                        <div
                                                            style={{ color: '#4a5568', fontSize: '13px', lineHeight: '1.4' }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: sanitizeHtml(item.description || item.actions || '')
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {item.type && (
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '3px 10px',
                                                                        borderRadius: '999px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        backgroundColor: item.type === 'Improvement' ? '#FFC72C' : item.type === 'New Feature' ? '#40C057' : item.type === 'Design' ? '#D0BFFF' : item.type === 'Bug Fix' ? '#F87171' : item.type === 'Announcement' ? '#60A5FA' : '#E2E8F0',
                                                                        color: (item.type === 'New Feature' || item.type === 'Bug Fix' || item.type === 'Announcement') ? '#ffffff' : item.type === 'Design' ? '#2E1065' : '#0f172a'
                                                                    }}>
                                                                        {item.type}
                                                                    </span>
                                                                )}
                                                                {item.platform && (
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '3px 10px',
                                                                        borderRadius: '999px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 500,
                                                                        backgroundColor: '#EEF2F6',
                                                                        color: '#495057'
                                                                    }}>
                                                                        {item.platform}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', color: '#64748b' }}>
                                                                <span>👍 {item.likes || 0}</span>
                                                                <span>👎 {item.dislikes || 0}</span>
                                                                {Array.isArray(item.feedbacks) && item.feedbacks.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setViewingFeedbackItem(item)}
                                                                        style={{
                                                                            background: '#e0f2fe',
                                                                            color: '#0369a1',
                                                                            border: '1px solid #bae6fd',
                                                                            padding: '1px 7px',
                                                                            borderRadius: '999px',
                                                                            fontWeight: 600,
                                                                            cursor: 'pointer',
                                                                            fontSize: '11px'
                                                                        }}
                                                                        title="Click to view feedbacks"
                                                                    >
                                                                        💬 {item.feedbacks.length}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="date-cell">{item.date}</td>
                                                    <td>
                                                        {item.media ? (
                                                            item.mediaType === 'video' || isWhatsNewVideo(item.media) ? (
                                                                <span style={{ fontSize: '18px', display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Video attachment">
                                                                    🎥 <span style={{ fontSize: '11px', color: '#475569', fontWeight: 600 }}>Video</span>
                                                                </span>
                                                            ) : (
                                                                <img
                                                                    src={item.media}
                                                                    alt=""
                                                                    style={{ width: '44px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                                                />
                                                            )
                                                        ) : (
                                                            <span style={{ color: '#a0aec0', fontSize: '12px' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td className="actions-cell">
                                                        <button
                                                            className="action-btn edit-btn"
                                                            onClick={() => handleEditWhatsNewClick(item)}
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            className="action-btn delete-btn"
                                                            onClick={() => handleDeleteWhatsNew(item.id || item._id)}
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Add/Edit What's New Modal */}
                    {(isAddingWhatsNew || isEditingWhatsNew) && (
                        <div className="modal-overlay">
                            <div className="blog-modal-content">
                                <div className="modal-header">
                                    <h2>{isAddingWhatsNew ? "Add What's New" : "Edit What's New"}</h2>
                                    <button className="close-btn" onClick={resetWhatsNewForm} disabled={isSavingWhatsNew}>×</button>
                                </div>
                                <form className="blog-form" onSubmit={handleSaveWhatsNew}>
                                    <div className="form-group">
                                        <label>Feature Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={whatsNewFormData.title}
                                            onChange={handleWhatsNewInputChange}
                                            placeholder="e.g. Multi-Calendar Sync & Real-Time Conflict Alerts"
                                            required
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Category *</label>
                                            <div className="wn-category-selection">
                                                <label className={`wn-category-card ${whatsNewFormData.category === 'Individual' ? 'selected' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="category"
                                                        value="Individual"
                                                        checked={whatsNewFormData.category === 'Individual'}
                                                        onChange={handleWhatsNewInputChange}
                                                    />
                                                    <span>Individual</span>
                                                </label>
                                                <label className={`wn-category-card ${whatsNewFormData.category === 'Organiser' ? 'selected' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="category"
                                                        value="Organiser"
                                                        checked={whatsNewFormData.category === 'Organiser'}
                                                        onChange={handleWhatsNewInputChange}
                                                    />
                                                    <span>Organiser</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Update Type *</label>
                                            <select
                                                name="type"
                                                value={isCustomType ? 'Other' : (whatsNewFormData.type || '')}
                                                onChange={handleTypeSelectChange}
                                                required
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 12px',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e2e8f0',
                                                    background: '#fff',
                                                    color: (!isCustomType && !whatsNewFormData.type) ? '#94a3b8' : '#1e293b'
                                                }}
                                            >
                                                <option value="" disabled hidden>Select Update Type</option>
                                                <option value="New Feature">New Feature</option>
                                                <option value="Improvement">Improvement</option>
                                                <option value="Design">Design</option>
                                                <option value="Bug Fix">Bug Fix</option>
                                                <option value="Announcement">Announcement</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {isCustomType && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom update type (e.g. Security, Performance)..."
                                                    value={customUpdateType}
                                                    onChange={handleCustomTypeInputChange}
                                                    style={{ marginTop: '8px', width: '100%', height: '40px', padding: '0 12px', borderRadius: '8px', border: '2px solid #3b82f6', background: '#fff' }}
                                                    required
                                                    autoFocus
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Platform Metadata *</label>
                                            <select
                                                name="platform"
                                                value={isCustomPlatform ? 'Other' : (whatsNewFormData.platform || '')}
                                                onChange={handlePlatformSelectChange}
                                                required
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 12px',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e2e8f0',
                                                    background: '#fff',
                                                    color: (!isCustomPlatform && !whatsNewFormData.platform) ? '#94a3b8' : '#1e293b'
                                                }}
                                            >
                                                <option value="" disabled hidden>Select Platform</option>
                                                <option value="Web">Web</option>
                                                <option value="Mobile">Mobile</option>
                                                <option value="Web & Mobile">Web & Mobile</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {isCustomPlatform && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom platform..."
                                                    value={customPlatform}
                                                    onChange={handleCustomPlatformInputChange}
                                                    style={{ marginTop: '8px', width: '100%', height: '40px', padding: '0 12px', borderRadius: '8px', border: '2px solid #3b82f6', background: '#fff' }}
                                                    required
                                                    autoFocus
                                                />
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label>Date *</label>
                                            <input
                                                type="text"
                                                name="date"
                                                value={whatsNewFormData.date}
                                                onChange={handleWhatsNewInputChange}
                                                placeholder="e.g. September 25, 2026"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Short Description *</label>
                                        <div style={whatsNewDescriptionError ? { border: '2px solid #ef4444', borderRadius: '10px' } : undefined}>
                                            <RichTextEditor
                                                value={whatsNewFormData.description}
                                                onChange={handleWhatsNewDescriptionChange}
                                                placeholder="Provide a concise 1-2 sentence overview of this update..."
                                                className="whats-new-editor"
                                            />
                                        </div>
                                        {whatsNewDescriptionError && (
                                            <span style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', display: 'block', fontWeight: 500 }}>
                                                Short Description is compulsory. Please enter a short description.
                                            </span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Action Items / Key Changes</label>
                                        <RichTextEditor
                                            value={whatsNewFormData.actions}
                                            onChange={handleWhatsNewActionsChange}
                                            placeholder="• Bullet points or list of specific actions taken in this release..."
                                            className="whats-new-editor"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Screenshot, GIF, or Video (Optional)</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <input
                                                type="text"
                                                name="media"
                                                value={whatsNewFormData.media}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setVideoPreviewError(false);
                                                    setWhatsNewFormData(prev => ({
                                                        ...prev,
                                                        media: val,
                                                        mediaType: val ? (isWhatsNewVideo(val) ? 'video' : 'image') : 'none'
                                                    }));
                                                }}
                                                placeholder="Enter image/GIF, direct video (.mp4, .webm) or YouTube/Vimeo/Loom URL..."
                                            />
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                💡 Supported: Direct video link (e.g. MP4/WebM), YouTube/Vimeo/Loom URL, or upload directly from your device.
                                            </span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <label className="file-input-label" style={{
                                                    padding: '8px 16px',
                                                    background: '#f1f5f9',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 500
                                                }}>
                                                    📁 Upload Image / Video
                                                    <input
                                                        type="file"
                                                        accept="image/*,video/*"
                                                        onChange={(e) => {
                                                            setVideoPreviewError(false);
                                                            handleWhatsNewMediaUpload(e);
                                                        }}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                                {whatsNewFormData.media && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setVideoPreviewError(false);
                                                            setWhatsNewFormData(prev => ({ ...prev, media: '', mediaType: 'none' }));
                                                        }}
                                                        style={{
                                                            padding: '6px 12px',
                                                            background: '#fee2e2',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            color: '#dc2626',
                                                            cursor: 'pointer',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        Clear Media
                                                    </button>
                                                )}
                                            </div>
                                            {whatsNewFormData.media && (
                                                <div style={{ marginTop: '8px' }}>
                                                    {(() => {
                                                        const embedUrl = getWhatsNewEmbedUrl(whatsNewFormData.media);
                                                        const isVid = whatsNewFormData.mediaType === 'video' || isWhatsNewVideo(whatsNewFormData.media);
                                                        if (embedUrl) {
                                                            return (
                                                                <iframe
                                                                    src={embedUrl}
                                                                    title="Video Preview"
                                                                    style={{ width: '100%', height: '200px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                    allowFullScreen
                                                                />
                                                            );
                                                        }
                                                        if (isVid) {
                                                            return (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    <video
                                                                        key={whatsNewFormData.media}
                                                                        src={whatsNewFormData.media}
                                                                        controls
                                                                        playsInline
                                                                        preload="auto"
                                                                        onError={() => setVideoPreviewError(true)}
                                                                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', background: '#000', display: 'block' }}
                                                                    />
                                                                    {videoPreviewError && (
                                                                        <div style={{
                                                                            padding: '8px 12px',
                                                                            background: '#fef2f2',
                                                                            border: '1px solid #fecaca',
                                                                            borderRadius: '6px',
                                                                            color: '#b91c1c',
                                                                            fontSize: '12px',
                                                                            lineHeight: '1.4'
                                                                        }}>
                                                                            ⚠️ <strong>Video Playback Blocked or Failed:</strong> The entered video URL could not be streamed by your browser (HTTP 403 Forbidden or CORS blocked by host). Try a direct public CDN MP4 (e.g. <code>https://vjs.zencdn.net/v/oceans.mp4</code>), a YouTube link, or click <strong>Upload Image / Video</strong>.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <img
                                                                src={whatsNewFormData.media}
                                                                alt="Preview"
                                                                style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', objectFit: 'contain', border: '1px solid #e2e8f0' }}
                                                            />
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-actions">
                                        <button type="button" className="cancel-btn" onClick={resetWhatsNewForm} disabled={isSavingWhatsNew}>Cancel</button>
                                        <button type="submit" className="submit-btn" disabled={isSavingWhatsNew}>
                                            {isSavingWhatsNew ? (
                                                <span className="btn-loading-content">
                                                    <span className="btn-spinner"></span>
                                                    <span>{isAddingWhatsNew ? "Adding What's New..." : "Saving Changes..."}</span>
                                                </span>
                                            ) : (
                                                isAddingWhatsNew ? "Add What's New" : "Save Changes"
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Mini Blog Preview Modal */}
            {viewingBlog && (
                <div className="blog-preview-overlay" onClick={() => setViewingBlog(null)}>
                    <div className="blog-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="blog-preview-close" onClick={() => setViewingBlog(null)}>×</button>
                        <div className="blog-preview-content">
                            {viewingBlog.image && viewingBlog.image.trim() !== '' && !viewingBlog.image.includes('UExBQ0VIT0xERVJfSU1BR0') && (
                                <img src={viewingBlog.image} alt={viewingBlog.title} className="blog-preview-image" />
                            )}
                            <div className="blog-preview-meta">
                                <span className="blog-preview-category">{viewingBlog.category}</span>
                                <span className="blog-preview-date">{viewingBlog.date}</span>
                            </div>
                            <h2 className="blog-preview-title">{viewingBlog.title}</h2>
                            {viewingBlog.summary && <p className="blog-preview-summary">{viewingBlog.summary}</p>}
                            <div className="blog-preview-body" {...richTextProps(viewingBlog.content)} />
                        </div>
                    </div>
                </div>
            )}
            {/* What's New Feedback Viewer Modal */}
            {viewingFeedbackItem && (
                <div className="modal-overlay" onClick={() => setViewingFeedbackItem(null)}>
                    <div className="blog-modal-content" style={{ maxWidth: '650px', background: '#ffffff', borderRadius: '16px', padding: '28px', color: '#1e293b' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>
                                    💬 User Feedback
                                </h3>
                                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                    For: <strong>{viewingFeedbackItem.title}</strong>
                                </p>
                            </div>
                            <button
                                onClick={() => setViewingFeedbackItem(null)}
                                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', fontSize: '18px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ maxHeight: '420px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {(!viewingFeedbackItem.feedbacks || viewingFeedbackItem.feedbacks.length === 0) ? (
                                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>No feedbacks submitted yet.</p>
                            ) : (
                                viewingFeedbackItem.feedbacks.map((fb, idx) => (
                                    <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px' }}>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#334155', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{fb.text}</p>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8' }}>
                                            <span>{fb.email ? `✉️ ${fb.email}` : '👤 Anonymous'}</span>
                                            <span>{fb.date || (fb.createdAt ? new Date(fb.createdAt).toLocaleDateString() : '')}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
